import { Button, Card, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import {
  useGetTeamMembersQuery,
  useGetTeamQuery,
  useUpdateTeamAdminsMutation,
} from '../../../../../api/teams'
import type { IProjectTeamMemberResource } from '../../../../../api/types/projects'
import { extractApiErrorMessage } from '../../../../../utils/api'

export const EditTeamAdministrationForm = () => {
  const { id } = useParams<{ id: string }>()
  const teamId = Number(id)
  const { data: team } = useGetTeamQuery({ id: teamId }, { skip: !id || Number.isNaN(teamId) })
  const permissions = team?.viewerPermissions

  const { data: members = [] } = useGetTeamMembersQuery(
    { teamId },
    { skip: !permissions?.can_manage_admins || !teamId }
  )

  const [updateAdmins, updateAdminsState] = useUpdateTeamAdminsMutation()

  const editableMembers = useMemo(
    () => members.filter((member: IProjectTeamMemberResource) => !member.is_author),
    [members]
  )

  const [adminIds, setAdminIds] = useState<number[]>([])

  useEffect(() => {
    const initial = editableMembers
      .filter((member: IProjectTeamMemberResource) => member.permission_role === 'admin')
      .map((member: IProjectTeamMemberResource) => member.user_id)
    setAdminIds(initial)
  }, [editableMembers])

  const toggleAdmin = (userId: number, checked: boolean) => {
    setAdminIds((prev) => {
      if (checked) {
        if (prev.includes(userId)) return prev
        if (prev.length >= 5) {
          toast.info('Не более 5 администраторов')
          return prev
        }
        return [...prev, userId]
      }
      return prev.filter((id) => id !== userId)
    })
  }

  const handleSaveAdmins = async () => {
    try {
      await updateAdmins({
        teamId,
        body: { admin_user_ids: adminIds },
      }).unwrap()
      toast.success('Администраторы обновлены')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить администраторов')
    }
  }

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
      <Stack gap={2}>
        <Typography variant="black-16-semiBold">Администрирование</Typography>
        <Typography variant="black-14-light" color="text.secondary">
          Администраторы могут редактировать описание, менять обложку и отвечать на приглашения в проекты.
        </Typography>

        {editableMembers.length === 0 && (
          <Typography variant="black-14-light">Нет участников для назначения администраторами.</Typography>
        )}

        {editableMembers.map((member) => {
          const userId = member.user_id
          const title =
            (member.user.fullname ?? member.user.shortname ?? '').trim() || `ID ${userId}`
          return (
            <FormControlLabel
              key={userId}
              control={
                <Switch
                  checked={adminIds.includes(userId)}
                  onChange={(event) => toggleAdmin(userId, event.target.checked)}
                />
              }
              label={title}
            />
          )
        })}

        <Stack direction="row" spacing={1}>
          <Button variant="follow" onClick={handleSaveAdmins} disabled={updateAdminsState.isLoading}>
            {updateAdminsState.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Сохранение...</span>
              </Stack>
            ) : (
              'Сохранить'
            )}
          </Button>
        </Stack>
      </Stack>
    </Card>
  )
}
